import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { message } from 'antd';
import axiosInstance from '../utils/api';

/**
 * Custom hook สำหรับจัดการข้อมูล Units
 * รองรับ caching และ real-time updates
 */
export const useUnits = (options = {}) => {
  const {
    category = '', // กรองตามหมวดหมู่
    activeOnly = true, // ดึงเฉพาะที่ใช้งานอยู่
    autoFetch = true, // เรียก API อัตโนมัติ
    enableCache = true // เปิดใช้งาน cache
  } = options;

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Cache key สำหรับ localStorage
  const cacheKey = `units_cache_${category}_${activeOnly}`;
  const cacheTimeKey = `${cacheKey}_time`;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 นาที

  /**
   * ตรวจสอบว่า cache ยังใช้ได้หรือไม่
   */
  const isCacheValid = useCallback(() => {
    if (!enableCache) return false;
    
    const cacheTime = localStorage.getItem(cacheTimeKey);
    if (!cacheTime) return false;
    
    const timeDiff = Date.now() - parseInt(cacheTime);
    return timeDiff < CACHE_DURATION;
  }, [cacheTimeKey, enableCache]);

  /**
   * โหลดข้อมูลจาก cache
   */
  const loadFromCache = useCallback(() => {
    if (!enableCache || !isCacheValid()) return null;
    
    try {
      const cachedData = localStorage.getItem(cacheKey);
      return cachedData ? JSON.parse(cachedData) : null;
    } catch (error) {
      console.warn('Failed to load units from cache:', error);
      return null;
    }
  }, [cacheKey, enableCache, isCacheValid]);

  /**
   * บันทึกข้อมูลลง cache
   */
  const saveToCache = useCallback((data) => {
    if (!enableCache) return;
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(cacheTimeKey, Date.now().toString());
    } catch (error) {
      console.warn('Failed to save units to cache:', error);
    }
  }, [cacheKey, cacheTimeKey, enableCache]);

  /**
   * ดึงข้อมูล units จาก API
   */
  const fetchUnits = useCallback(async (forceRefresh = false) => {
    // ถ้าไม่ force refresh ให้ลองโหลดจาก cache ก่อน
    if (!forceRefresh) {
      const cachedData = loadFromCache();
      if (cachedData) {
        setUnits(cachedData);
        setLastFetch(new Date());
        return cachedData;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = activeOnly ? '/api/units/active' : '/api/units';
      const params = {};
      
      if (category) {
        params.category = category;
      }
      
      if (!activeOnly) {
        params.limit = 100; // ขอข้อมูลเยอะหน่อยเพื่อลด API calls
      }

      const response = await axiosInstance.get(endpoint, { params });
      
      if (response.data.success) {
        const unitsData = response.data.data;
        setUnits(unitsData);
        setLastFetch(new Date());
        saveToCache(unitsData);
        return unitsData;
      } else {
        throw new Error(response.data.message || 'Failed to fetch units');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลหน่วยนับ';
      setError(errorMessage);
      message.error(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [category, activeOnly, loadFromCache, saveToCache]);

  /**
   * รีเฟรชข้อมูล (force reload)
   */
  const refreshUnits = useCallback(() => {
    return fetchUnits(true);
  }, [fetchUnits]);

  /**
   * ล้าง cache
   */
  const clearCache = useCallback(() => {
    if (enableCache) {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);
    }
  }, [cacheKey, cacheTimeKey, enableCache]);

  // Auto fetch เมื่อ mount หรือ dependencies เปลี่ยน
  useEffect(() => {
    if (autoFetch) {
      fetchUnits();
    }
  }, [autoFetch, fetchUnits]);

  return {
    units,
    loading,
    error,
    lastFetch,
    fetchUnits,
    refreshUnits,
    clearCache,
    isCacheValid: isCacheValid()
  };
};

/**
 * Custom hook สำหรับ Units Options ที่เหมาะกับ Ant Design Select
 * รองรับการแปลงเป็น format { value, label }
 */
export const useUnitsOptions = (options = {}) => {
  const {
    category = '',
    includeCode = true, // แสดงรหัสหน่วยใน label
    valueField = 'id', // field ที่ใช้เป็น value (id หรือ unit_name)
    sortBy = 'display_order' // เรียงตาม (display_order, unit_name, category)
  } = options;

  const { units, loading, error, refreshUnits } = useUnits({
    category,
    activeOnly: true,
    autoFetch: true,
    enableCache: true
  });

  /**
   * แปลง units เป็น options format สำหรับ Ant Design
   */
  const unitsOptions = useMemo(() => {
    if (!units.length) return [];

    let processedUnits = [...units];

    // เรียงข้อมูล
    processedUnits.sort((a, b) => {
      switch (sortBy) {
        case 'unit_name':
          return a.unit_name.localeCompare(b.unit_name);
        case 'category':
          return a.category.localeCompare(b.category) || 
                 (a.display_order - b.display_order);
        case 'display_order':
        default:
          return (a.display_order - b.display_order) || 
                 a.unit_name.localeCompare(b.unit_name);
      }
    });

    // แปลงเป็น options format
    return processedUnits.map(unit => {
      let label = unit.unit_name;
      
      // เพิ่มรหัสหน่วยใน label หากต้องการ
      if (includeCode && unit.unit_code) {
        label = `${unit.unit_name} (${unit.unit_code})`;
      }

      return {
        value: unit[valueField],
        label,
        unit: unit, // เก็บข้อมูลเต็มไว้ใช้งาน
        category: unit.category,
        code: unit.unit_code,
        displayOrder: unit.display_order
      };
    });
  }, [units, includeCode, valueField, sortBy]);

  /**
   * ค้นหา unit ตาม value
   */
  const findUnitByValue = useCallback((value) => {
    return units.find(unit => unit[valueField] === value) || null;
  }, [units, valueField]);

  /**
   * ค้นหา unit ตาม ID
   */
  const findUnitById = useCallback((id) => {
    return units.find(unit => unit.id === id) || null;
  }, [units]);

  /**
   * ดึงชื่อหน่วยตาม value
   */
  const getUnitName = useCallback((value) => {
    const unit = findUnitByValue(value);
    return unit ? unit.unit_name : '';
  }, [findUnitByValue]);

  /**
   * ดึงรหัสหน่วยตาม value
   */
  const getUnitCode = useCallback((value) => {
    const unit = findUnitByValue(value);
    return unit ? unit.unit_code : '';
  }, [findUnitByValue]);

  return {
    unitsOptions,
    units,
    loading,
    error,
    refreshUnits,
    findUnitByValue,
    findUnitById,
    getUnitName,
    getUnitCode,
    isEmpty: unitsOptions.length === 0
  };
};

/**
 * Custom hook สำหรับจัดการ Units Categories
 */
export const useUnitsCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/api/units/categories');
      
      if (response.data.success) {
        setCategories(response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch categories');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดหมวดหมู่หน่วยนับ';
      setError(errorMessage);
      message.error(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * แปลง categories เป็น options สำหรับ Select
   */
  const categoryOptions = useMemo(() => {
    return categories.map(cat => ({
      value: cat.category,
      label: `${getCategoryDisplayName(cat.category)} (${cat.active_count}/${cat.count})`,
      count: cat.count,
      activeCount: cat.active_count
    }));
  }, [categories]);

  return {
    categories,
    categoryOptions,
    loading,
    error,
    refreshCategories: fetchCategories
  };
};

/**
 * แปลงชื่อหมวดหมู่เป็นภาษาไทย
 */
export const getCategoryDisplayName = (category) => {
  const categoryNames = {
    count: 'หน่วยนับ',
    weight: 'น้ำหนัก',
    volume: 'ปริมาตร', 
    length: 'ความยาว',
    area: 'พื้นที่',
    time: 'เวลา',
    container: 'ภาชนะ'
  };
  
  return categoryNames[category] || category;
};

/**
 * Utility function สำหรับ migrate จาก hardcoded arrays
 * ช่วยในการแปลงข้อมูลเดิมที่เป็น string เป็น ID
 */
export const migrateUnitValue = (oldValue, units) => {
  if (!oldValue || !units.length) return null;
  
  // หา unit ที่ตรงกับชื่อเดิม
  const matchedUnit = units.find(unit => 
    unit.unit_name === oldValue || 
    unit.unit_code === oldValue
  );
  
  return matchedUnit ? matchedUnit.id : null;
};

export default useUnits;